import React, { useState, useEffect } from 'react';
import { deviceInstallationApi } from '../services/api';
import {
  PageHeader,
  LoadingSpinner,
  EmptyState,
  Badge,
  ErrorMessage,
  SuccessMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Button,
  FormSelect,
  ConfirmationModal,
  Modal,
  FormField,
  Card,
  CardBody
} from '../components/common';
import { Smartphone, Trash2, RefreshCw, Send } from 'lucide-react';

const DeviceInstallations = () => {
  // Matched registrations state
  const [matchedData, setMatchedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Stats state
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Delete modals
  const [deleteModal, setDeleteModal] = useState({ show: false, item: null });
  const [deleteAllOrphansModal, setDeleteAllOrphansModal] = useState(false);
  const [deletingOrphans, setDeletingOrphans] = useState(false);

  // Test notification modal
  const [testModal, setTestModal] = useState({ show: false, item: null });
  const [testMessage, setTestMessage] = useState('Test push notification from Reziland Admin');
  const [sendingTest, setSendingTest] = useState(false);

  const fetchMatchedData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await deviceInstallationApi.getMatchedRegistrations(environmentFilter || undefined);
      setMatchedData(response.data || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch matched registrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await deviceInstallationApi.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchMatchedData();
  }, [environmentFilter]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleDelete = async () => {
    const item = deleteModal.item;
    if (!item) return;
    try {
      if (item.matchStatus === 'Orphan') {
        await deviceInstallationApi.deleteAzureRegistration(item.azureRegistrationId);
        setSuccess('Orphaned Azure registration deleted');
      } else if (item.matchStatus === 'DbOnly') {
        await deviceInstallationApi.delete(item.dbUserDeviceId);
        setSuccess('DB-only device record deleted');
      } else {
        // Matched — delete DB record (which also removes Azure NH installation)
        await deviceInstallationApi.delete(item.dbUserDeviceId);
        setSuccess('Matched device deleted from DB and Azure NH');
      }
      fetchMatchedData();
      fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to delete');
    } finally {
      setDeleteModal({ show: false, item: null });
    }
  };

  const handleDeleteAllOrphans = async () => {
    const orphans = matchedData.filter(r => r.matchStatus === 'Orphan');
    if (orphans.length === 0) return;
    try {
      setDeletingOrphans(true);
      setError(null);
      const results = await Promise.allSettled(
        orphans.map(o => deviceInstallationApi.deleteAzureRegistration(o.azureRegistrationId))
      );
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) {
        setSuccess(`Deleted ${orphans.length - failed} orphans. ${failed} failed.`);
      } else {
        setSuccess(`All ${orphans.length} orphaned registrations deleted`);
      }
      fetchMatchedData();
      fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to delete orphans');
    } finally {
      setDeletingOrphans(false);
      setDeleteAllOrphansModal(false);
    }
  };

  const handleSendTest = async () => {
    if (!testModal.item?.userId || !testMessage.trim()) return;
    try {
      setSendingTest(true);
      setError(null);
      await deviceInstallationApi.sendTestNotification(testModal.item.userId, testMessage.trim());
      setSuccess(`Test notification sent to ${testModal.item.userFirstName || testModal.item.userId}`);
      setTestModal({ show: false, item: null });
    } catch (err) {
      setError(err.response?.data || err.message || 'Failed to send test notification');
    } finally {
      setSendingTest(false);
    }
  };

  const truncate = (str, len = 20) => {
    if (!str) return '—';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  // Client-side status filter
  const filteredData = statusFilter
    ? matchedData.filter(r => r.matchStatus === statusFilter)
    : matchedData;

  const statusCounts = {
    Matched: matchedData.filter(r => r.matchStatus === 'Matched').length,
    Orphan: matchedData.filter(r => r.matchStatus === 'Orphan').length,
    DbOnly: matchedData.filter(r => r.matchStatus === 'DbOnly').length,
  };

  const statCards = [
    { label: 'Total Devices', value: stats?.totalDevices ?? '—', color: 'text-dark-900' },
    { label: 'Active', value: stats?.activeDevices ?? '—', color: 'text-green-600' },
    { label: 'iOS', value: stats?.iosDevices ?? '—', color: 'text-blue-600' },
    { label: 'Android', value: stats?.androidDevices ?? '—', color: 'text-emerald-600' },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Matched': return <Badge variant="accent">Matched</Badge>;
      case 'Orphan': return <Badge variant="danger">Orphan</Badge>;
      case 'DbOnly': return <Badge variant="warning">DB Only</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const getEnvBadge = (env) => {
    switch (env) {
      case 'dev': return <Badge variant="secondary">dev</Badge>;
      case 'prod': return <Badge variant="primary">prod</Badge>;
      default: return <Badge variant="ghost">unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Device Installations"
        subtitle="Unified view of push notification registrations — DB and Azure NH matched"
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess(null)} />}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardBody className="py-4 px-5">
              <p className="text-sm font-medium text-gray-500">{stat.label}</p>
              <p className={`text-2xl font-bold mt-1 ${stat.color}`}>
                {statsLoading ? <LoadingSpinner size="small" /> : stat.value}
              </p>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Match status summary */}
      {!loading && (
        <div className="flex gap-4 text-sm">
          <span className="text-green-700 font-medium">{statusCounts.Matched} Matched</span>
          <span className="text-red-600 font-medium">{statusCounts.Orphan} Orphans</span>
          <span className="text-yellow-600 font-medium">{statusCounts.DbOnly} DB Only</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex gap-3">
          <FormSelect
            value={environmentFilter}
            onChange={(e) => setEnvironmentFilter(e.target.value)}
            options={[
              { value: '', label: 'All Environments' },
              { value: 'dev', label: 'Dev' },
              { value: 'prod', label: 'Prod' },
              { value: 'unknown', label: 'Unknown (legacy)' },
            ]}
          />
          <FormSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'Matched', label: 'Matched' },
              { value: 'Orphan', label: 'Orphan' },
              { value: 'DbOnly', label: 'DB Only' },
            ]}
          />
        </div>
        <div className="flex gap-3">
          {statusCounts.Orphan > 0 && (
            <Button
              variant="danger"
              onClick={() => setDeleteAllOrphansModal(true)}
              disabled={deletingOrphans}
            >
              <Trash2 size={16} className="mr-2" />
              Delete All Orphans ({statusCounts.Orphan})
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => { fetchMatchedData(); fetchStats(); }}
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Unified Table */}
      {loading ? (
        <LoadingSpinner size="large" />
      ) : filteredData.length === 0 ? (
        <EmptyState
          icon={Smartphone}
          title="No registrations found"
          message="No device registrations match your filters."
        />
      ) : (
        <Table>
          <TableHeader
            columns={['Status', 'User', 'Environment', 'Platform', 'Device Token', 'DB Active', 'Azure Expiry', 'Actions']}
          />
          <TableBody>
            {filteredData.map((item, idx) => (
              <TableRow key={`${item.matchStatus}-${item.azureRegistrationId || item.dbUserDeviceId}-${idx}`}>
                <TableCell>
                  {getStatusBadge(item.matchStatus)}
                </TableCell>
                <TableCell>
                  {item.userFirstName || item.userLastName ? (
                    <div>
                      <p className="font-medium text-dark-900">
                        {item.userFirstName} {item.userLastName}
                      </p>
                      <p className="text-xs text-gray-500">{item.userEmail}</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500">Unknown</p>
                      {item.userId && (
                        <p className="text-xs text-gray-400 font-mono" title={item.userId}>
                          {truncate(item.userId, 16)}
                        </p>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {getEnvBadge(item.azureEnvironment)}
                </TableCell>
                <TableCell>
                  <Badge variant={
                    (item.azurePlatform?.includes('iOS') || item.dbDeviceType?.toLowerCase() === 'ios')
                      ? 'primary' : 'accent'
                  }>
                    {item.azurePlatform || item.dbDeviceType?.toUpperCase() || 'Unknown'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs text-gray-600" title={item.azurePushChannel || item.dbDeviceToken}>
                    {truncate(item.azurePushChannel || item.dbDeviceToken, 20)}
                  </span>
                </TableCell>
                <TableCell>
                  {item.dbIsActive != null ? (
                    <Badge variant={item.dbIsActive ? 'accent' : 'danger'}>
                      {item.dbIsActive ? 'Active' : 'Inactive'}
                    </Badge>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-600">
                    {formatDate(item.azureExpirationTime)}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {item.userId && item.matchStatus !== 'Orphan' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        title="Send test notification"
                        onClick={() => {
                          setTestMessage('Test push notification from Reziland Admin');
                          setTestModal({ show: true, item });
                        }}
                      >
                        <Send size={14} />
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteModal({ show: true, item })}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Multi-device users section */}
      {stats?.usersWithMultipleDevices?.length > 0 && (
        <Card>
          <CardBody>
            <h3 className="text-lg font-semibold text-dark-900 mb-4">Users with Multiple Devices</h3>
            <div className="space-y-2">
              {stats.usersWithMultipleDevices.map((user) => (
                <div key={user.userId} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-dark-900">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="text-sm text-gray-500 ml-2">{user.email}</span>
                  </div>
                  <Badge variant="secondary">{user.deviceCount} devices</Badge>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <ConfirmationModal
        isOpen={deleteAllOrphansModal}
        onClose={() => setDeleteAllOrphansModal(false)}
        onConfirm={handleDeleteAllOrphans}
        title="Delete All Orphaned Registrations"
        message={`Are you sure you want to delete all ${statusCounts.Orphan} orphaned Azure NH registrations? These registrations have no matching DB record and will stop receiving push notifications.`}
        confirmText={deletingOrphans ? 'Deleting...' : 'Delete All'}
        confirmVariant="danger"
      />

      <ConfirmationModal
        isOpen={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, item: null })}
        onConfirm={handleDelete}
        title={
          deleteModal.item?.matchStatus === 'Orphan'
            ? 'Delete Orphaned Azure Registration'
            : deleteModal.item?.matchStatus === 'DbOnly'
              ? 'Delete DB-Only Device Record'
              : 'Delete Matched Device'
        }
        message={
          deleteModal.item?.matchStatus === 'Orphan'
            ? 'This registration exists only in Azure Notification Hub (no matching DB record). Deleting it will stop push notifications for this registration.'
            : deleteModal.item?.matchStatus === 'DbOnly'
              ? 'This record exists only in the database (no matching Azure registration). It will be removed from the database.'
              : 'This device is matched in both DB and Azure NH. Deleting will remove it from both.'
        }
        confirmText="Delete"
        confirmVariant="danger"
      />

      <Modal
        isOpen={testModal.show}
        onClose={() => setTestModal({ show: false, item: null })}
        title="Send Test Notification"
        size="small"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-500">Sending to</p>
            <p className="font-medium text-dark-900">
              {testModal.item?.userFirstName} {testModal.item?.userLastName}
            </p>
            <p className="text-sm text-gray-500">{testModal.item?.userEmail}</p>
          </div>
          <FormField
            label="Message"
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="Enter notification message..."
          />
          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => setTestModal({ show: false, item: null })}
              disabled={sendingTest}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSendTest}
              disabled={!testMessage.trim() || sendingTest}
              loading={sendingTest}
            >
              <Send size={16} className="mr-2" />
              Send
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DeviceInstallations;
